import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Building2, Plus, X, Trash2, FileText, Mail, UserPlus, Download, ExternalLink, Copy, MessageSquare } from "lucide-react";
import PaymentSelector from "@/components/PaymentSelector";
import CorpPaymentMethods from "@/components/CorpPaymentMethods";
import CorpInvoices from "@/components/CorpInvoices";
import { StaffThemeProvider, StaffThemeToggle } from "@/lib/StaffTheme";

export default function Corporate() {
  return (
    <StaffThemeProvider>
      <CorporateInner/>
    </StaffThemeProvider>
  );
}

function CorporateInner() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("accounts"); // accounts | mine
  const [accounts, setAccounts] = useState([]);
  const [myAccounts, setMyAccounts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openAccount, setOpenAccount] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const refresh = async () => {
    setBusy(true);
    try {
      const mine = await api.get("/corporate/me");
      setMyAccounts(mine.data);
      if (user?.role === "dispatcher") {
        const all = await api.get("/corporate/accounts");
        setAccounts(all.data);
      }
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  if (!user) return null;
  const isDispatcher = user.role === "dispatcher";

  return (
    <div data-testid="corporate-page" className="bg-[#050505] min-h-screen text-[#F5F2EB]">
      <Navbar/>
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline mb-2">Corporate accounts</div>
              <h1 className="font-serif text-4xl md:text-5xl leading-none">Business travel.</h1>
              <p className="text-[#A1A1AA] mt-3 max-w-xl">Multi-employee accounts with consolidated monthly invoicing and volume discounts.</p>
            </div>
            {isDispatcher && (
              <div className="flex items-center gap-2">
                <StaffThemeToggle compact/>
                <button data-testid="new-corp-btn" onClick={() => setCreating(true)} className="btn-gold"><Plus size={14}/> New account</button>
              </div>
            )}
          </div>

          {isDispatcher && (
            <div className="flex border-b border-white/10 mb-8">
              <button data-testid="tab-accounts" onClick={() => setTab("accounts")} className={tabClass(tab === "accounts")}>All accounts ({accounts.length})</button>
              <button data-testid="tab-mine" onClick={() => setTab("mine")} className={tabClass(tab === "mine")}>My membership ({myAccounts.length})</button>
            </div>
          )}

          {busy && <div className="text-[#A1A1AA]">Loading…</div>}

          {/* All accounts (dispatcher view) */}
          {!busy && isDispatcher && tab === "accounts" && (
            <>
              {accounts.length === 0 ? (
                <Empty isDispatcher onNew={() => setCreating(true)}/>
              ) : (
                <div className="grid gap-4">
                  {accounts.map(a => <CorpRow key={a.account_id} a={a} onOpen={() => setOpenAccount(a)}/>)}
                </div>
              )}
            </>
          )}

          {/* My memberships (any user) */}
          {!busy && (!isDispatcher || tab === "mine") && (
            <>
              {myAccounts.length === 0 ? (
                isDispatcher ? (
                  <div className="border border-white/10 bg-[#0A0A0A] p-12 text-center">
                    <Building2 size={32} className="text-[#D4AF37] mx-auto mb-4"/>
                    <div className="font-serif text-2xl mb-2">No corporate accounts.</div>
                    <p className="text-[#A1A1AA] text-sm mb-2">Ask your office administrator to add your email to a Delux corporate account.</p>
                  </div>
                ) : (
                  <RegisterCompany userEmail={user.email} onRegistered={refresh}/>
                )
              ) : (
                <div className="grid gap-4">
                  {myAccounts.map(a => <CorpRow key={a.account_id} a={a} onOpen={() => setOpenAccount(a)}/>)}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {creating && <NewAccountModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); refresh(); }}/>}
      {openAccount && <AccountDrawer account={openAccount} isDispatcher={isDispatcher} userEmail={user.email} onClose={() => setOpenAccount(null)} onChange={refresh}/>}
      <Footer/>
    </div>
  );
}

const tabClass = (a) => `px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition border-b-2 ${a ? 'border-[#D4AF37] bg-[#D4AF37] text-[#0A0A0A] font-bold shadow-[0_0_14px_rgba(212,175,55,0.45)]' : 'border-transparent text-[#A1A1AA] hover:text-white hover:bg-white/5'}`;

/** Passenger self-serve: register a company from the dashboard. The passenger
 *  becomes corp admin + first member. At booking time they choose per trip:
 *  personal account or the corporate account. */
function RegisterCompany({ userEmail, onRegistered }) {
  const [f, setF] = useState({ company_name: "", billing_email: userEmail || "", billing_address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));
  const inputCls = "w-full bg-black border border-white/15 rounded px-3 py-3 text-sm text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/corporate/register", f);
      toast.success(`${f.company_name} registered — business trips can now be billed to your company`);
      onRegistered?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not register your company");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="register-company-card">
      <div className="border border-white/10 bg-[#0A0A0A] p-8">
        <Building2 size={28} className="text-[#D4AF37] mb-4"/>
        <h2 className="font-serif text-2xl mb-3">Set up your company account</h2>
        <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
          Travel for work? Register your company and keep business rides separate from personal ones:
        </p>
        <ul className="text-sm text-[#A1A1AA] space-y-2">
          <li>• When booking, choose <span className="text-[#D4AF37]">Personal</span> or <span className="text-[#D4AF37]">Business</span> per trip</li>
          <li>• Business trips roll into one consolidated invoice</li>
          <li>• Add teammates' emails so their rides bill to the company too</li>
          <li>• Volume discounts available — dispatch sets your rate</li>
        </ul>
        <p className="text-xs text-[#71717a] mt-5">Already on a company account? Ask your office admin to add <span className="text-[#F5F2EB]">{userEmail}</span>.</p>
      </div>
      <form onSubmit={submit} className="border border-white/10 bg-[#0A0A0A] p-8 space-y-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-1.5">Company name *</label>
          <input data-testid="corp-reg-name" required className={inputCls} value={f.company_name} onChange={set("company_name")} placeholder="Acme Manufacturing LLC"/>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-1.5">Billing email</label>
          <input data-testid="corp-reg-email" type="email" className={inputCls} value={f.billing_email} onChange={set("billing_email")}/>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-1.5">Billing address</label>
          <input data-testid="corp-reg-address" className={inputCls} value={f.billing_address} onChange={set("billing_address")} placeholder="Street, city, state"/>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-1.5">Notes for dispatch</label>
          <input data-testid="corp-reg-notes" className={inputCls} value={f.notes} onChange={set("notes")} placeholder="e.g. ~10 airport runs / month"/>
        </div>
        <button data-testid="corp-reg-submit" disabled={busy} className="btn-gold w-full !py-3.5 text-xs">
          {busy ? "Registering…" : "Register my company"}
        </button>
      </form>
    </div>
  );
}

function Empty({ isDispatcher, onNew }) {
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-12 text-center">
      <Building2 size={32} className="text-[#D4AF37] mx-auto mb-4"/>
      <div className="font-serif text-2xl mb-2">No corporate accounts yet.</div>
      <p className="text-[#A1A1AA] text-sm mb-6 max-w-md mx-auto">Set up your first corporate account to enable consolidated billing for a company's travel team.</p>
      {isDispatcher && <button data-testid="empty-new-corp-btn" onClick={onNew} className="btn-gold"><Plus size={14}/> Create account</button>}
    </div>
  );
}

function CorpRow({ a, onOpen }) {
  return (
    <button
      data-testid={`corp-row-${a.account_id}`}
      onClick={onOpen}
      className="w-full text-left border border-white/10 bg-[#0A0A0A] p-6 hover:border-[#D4AF37]/40 transition flex flex-col md:flex-row md:items-center gap-4"
    >
      <div className="flex-1">
        <div className="overline mb-1">{a.billing_period} billing</div>
        <div className="font-serif text-xl">{a.name}</div>
        <div className="text-xs text-[#A1A1AA] mt-1 flex items-center gap-2"><Mail size={12}/>{a.billing_email}</div>
      </div>
      <div className="text-right">
        <div className="font-serif text-2xl text-[#D4AF37]">{a.discount_percent}%</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">corporate discount</div>
        <div className="text-xs text-[#A1A1AA] mt-1">{(a.employee_emails || []).length} employees</div>
      </div>
    </button>
  );
}

function NewAccountModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [discount, setDiscount] = useState(10);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name || !billingEmail) return toast.error("Name and billing email required");
    setBusy(true);
    try {
      await api.post("/corporate/accounts", {
        name, billing_email: billingEmail, billing_address: billingAddress,
        discount_percent: Number(discount), billing_period: billingPeriod,
      });
      toast.success(`${name} created`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}/>
      <div data-testid="new-corp-modal" className="relative w-full max-w-md bg-[#0A0A0A] border border-white/10 p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="overline">New corporate account</div>
            <div className="font-serif text-2xl">Setup</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA]"><X size={18}/></button>
        </div>
        <div className="space-y-5">
          <Field label="Company name"><input data-testid="corp-name" className="field-input" value={name} onChange={e => setName(e.target.value)} placeholder="Acme Corporation"/></Field>
          <Field label="Billing email"><input data-testid="corp-billing-email" type="email" className="field-input" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} placeholder="ap@company.com"/></Field>
          <Field label="Billing address"><input data-testid="corp-billing-address" className="field-input" value={billingAddress} onChange={e => setBillingAddress(e.target.value)} placeholder="123 Main St, Milwaukee WI"/></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Discount %"><input data-testid="corp-discount" type="number" min={0} max={50} className="field-input" value={discount} onChange={e => setDiscount(e.target.value)}/></Field>
            <Field label="Billing cycle">
              <select data-testid="corp-period" className="field-input bg-black" value={billingPeriod} onChange={e => setBillingPeriod(e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="biweekly">Biweekly</option>
              </select>
            </Field>
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost !py-2.5 !px-4 text-[11px]">Cancel</button>
          <button data-testid="save-corp-btn" disabled={busy} onClick={submit} className="btn-gold !py-2.5 !px-4 text-[11px]">{busy ? "Saving…" : "Create account"}</button>
        </div>
      </div>
    </div>
  );
}

function AccountDrawer({ account, isDispatcher, userEmail, onClose, onChange }) {
  const [employees, setEmployees] = useState(account.employee_emails || []);
  const [newEmail, setNewEmail] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [invMonth, setInvMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);

  // Inline-edit state for account metadata. Anyone allowed to manage the
  // account (dispatcher OR an employee) can change these; discount % is
  // server-side restricted to dispatchers.
  const [edit, setEdit] = useState({
    name: account.name || "",
    billing_email: account.billing_email || "",
    billing_address: account.billing_address || "",
    billing_period: account.billing_period || "monthly",
    discount_percent: account.discount_percent ?? 0,
  });
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Both dispatcher AND a verified employee (whose email is on the roster)
  // can manage employees, billing details, etc. Dispatcher-only buttons are
  // gated separately below.
  const canManage = isDispatcher || (userEmail && employees.map(e => e.toLowerCase()).includes(userEmail.toLowerCase()));

  const updField = (k, v) => setEdit(p => ({ ...p, [k]: v }));

  const saveDetails = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        name: edit.name,
        billing_email: edit.billing_email,
        billing_address: edit.billing_address,
        billing_period: edit.billing_period,
      };
      // Discount % is dispatcher-only — only include it when allowed
      if (isDispatcher) payload.discount_percent = Number(edit.discount_percent);
      const r = await api.patch(`/corporate/accounts/${account.account_id}`, payload);
      if (r.data.account) {
        // Mutate the existing account object so the drawer reflects the new
        // values without needing a full refresh
        Object.assign(account, r.data.account);
      }
      toast.success("Account updated");
      setEditOpen(false);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingEdit(false); }
  };

  const addEmp = async () => {
    if (!newEmail) return;
    try {
      const r = await api.post(`/corporate/accounts/${account.account_id}/employees`, { email: newEmail });
      setEmployees([...employees, r.data.email]);
      setNewEmail("");
      toast.success("Employee added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeEmp = async (email) => {
    try {
      await api.delete(`/corporate/accounts/${account.account_id}/employees/${encodeURIComponent(email)}`);
      setEmployees(employees.filter(e => e !== email));
      toast.success("Employee removed");
    } catch (e) { toast.error("Failed"); }
  };

  const deleteAcc = async () => {
    if (!window.confirm("Delete this corporate account? This will not affect existing bookings.")) return;
    try {
      await api.delete(`/corporate/accounts/${account.account_id}`);
      toast.success("Account deleted");
      onChange();
      onClose();
    } catch (e) { toast.error("Failed"); }
  };

  const [invPeriodType, setInvPeriodType] = useState("month"); // "month" | "week" | "range"
  const [invWeekStart, setInvWeekStart] = useState(() => {
    const d = new Date();
    // Default to Monday of this week
    const day = (d.getDay() + 6) % 7;  // 0 = Mon
    d.setDate(d.getDate() - day);
    return d.toISOString().slice(0, 10);
  });
  const [invRangeStart, setInvRangeStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [invRangeEnd, setInvRangeEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedTripIds, setSelectedTripIds] = useState(new Set());
  const [billing, setBilling] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [invoicesRefresh, setInvoicesRefresh] = useState(0);
  const [payTrip, setPayTrip] = useState(null);  // booking object to take payment for

  const loadInvoice = async () => {
    setBusy(true);
    setSelectedTripIds(new Set());
    try {
      const params = invPeriodType === "month"
        ? { month: invMonth }
        : invPeriodType === "week"
          ? { period_type: "week", start: invWeekStart }
          : { period_type: "range", start: invRangeStart, end: invRangeEnd };
      const r = await api.get(`/corporate/accounts/${account.account_id}/invoice`, { params });
      setInvoice(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load invoice"); } finally { setBusy(false); }
  };

  const toggleTrip = (id) => {
    setSelectedTripIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAllUnpaid = () => {
    if (!invoice) return;
    setSelectedTripIds(new Set(invoice.rides.filter(r => r.payment_status !== "paid").map(r => r.booking_id)));
  };
  const clearSelection = () => setSelectedTripIds(new Set());

  const createCustomInvoice = async () => {
    if (selectedTripIds.size === 0) {
      toast.error("Tick at least one trip first");
      return;
    }
    setBilling(true);
    try {
      const r = await api.post("/invoices/consolidated", {
        customer_email: account.billing_email,
        customer_name: account.name,
        trip_ids: Array.from(selectedTripIds),
      });
      toast.success(`Invoice ${r.data.invoice_number} created — ${selectedTripIds.size} trip(s) bundled`);
      setCreatedInvoice(r.data);  // surface in the drawer with Pay/Copy buttons
      setInvoicesRefresh(n => n + 1);  // refresh the Invoices list (one-click charge)
      // Auto-send to the billing email
      try {
        await api.post(`/invoices/consolidated/${r.data.invoice_id}/email`);
        toast.success(`Pay link emailed to ${account.billing_email}`);
      } catch (_) { /* email may be MOCKED — ignore */ }
      setSelectedTripIds(new Set());
      loadInvoice();  // refresh the list to show invoice attachment
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setBilling(false);
    }
  };

  const downloadCsv = () => {
    if (!invoice) return;
    const rows = [
      ["Date", "Confirmation", "Passenger", "Vehicle", "From", "To", "Fare", "Payment Status", "Payment Method", "Transaction ID"],
      ...invoice.rides.map(r => [
        new Date(r.pickup_datetime).toLocaleString(),
        r.confirmation_code,
        r.customer_name,
        r.vehicle_name,
        r.pickup_address,
        r.dropoff_address || "",
        r.fare,
        r.payment_status || "",
        r.payment_method || "",
        r.payment_id || "",
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `delux-invoice-${account.name.replace(/\s+/g,'_')}-${invoice.period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="corp-account-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-[#0A0A0A] border border-white/10 rounded-lg overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-[#0A0A0A] z-10 p-6 border-b border-white/10 flex justify-between">
          <div>
            <div className="overline">Corporate account</div>
            <div className="font-serif text-2xl mt-1">{account.name}</div>
            <div className="text-xs text-[#A1A1AA] mt-1">{account.billing_email}</div>
          </div>
          <button data-testid="corp-drawer-close" onClick={onClose} className="text-[#A1A1AA]"><X size={18}/></button>
        </div>
        <div className="p-6 space-y-7">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Details</div>
              {canManage && (
                <button
                  data-testid="edit-details-toggle"
                  onClick={() => setEditOpen(v => !v)}
                  className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline"
                >{editOpen ? "Cancel" : "Edit"}</button>
              )}
            </div>
            {!editOpen ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-[#71717A] text-xs">Discount</div><div className="text-[#D4AF37] font-serif text-xl">{account.discount_percent || 0}%</div></div>
                <div><div className="text-[#71717A] text-xs">Cycle</div><div className="font-serif text-xl">{account.billing_period}</div></div>
                {account.billing_address && <div className="col-span-2"><div className="text-[#71717A] text-xs">Billing address</div><div>{account.billing_address}</div></div>}
                <div className="col-span-2"><div className="text-[#71717A] text-xs">Billing email</div><div className="text-[#F5F2EB]">{account.billing_email}</div></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm" data-testid="edit-details-form">
                <Field label="Account name">
                  <input data-testid="edit-name" className="field-input" value={edit.name} onChange={e => updField("name", e.target.value)}/>
                </Field>
                <Field label="Billing email">
                  <input data-testid="edit-billing-email" type="email" className="field-input" value={edit.billing_email} onChange={e => updField("billing_email", e.target.value)}/>
                </Field>
                <Field label={isDispatcher ? "Discount %" : "Discount % (dispatcher only)"}>
                  <input
                    data-testid="edit-discount"
                    type="number"
                    min={0} max={50} step={0.5}
                    className="field-input disabled:opacity-50"
                    value={edit.discount_percent}
                    disabled={!isDispatcher}
                    onChange={e => updField("discount_percent", e.target.value)}
                  />
                </Field>
                <Field label="Billing cycle">
                  <select
                    data-testid="edit-cycle"
                    className="field-input"
                    value={edit.billing_period}
                    onChange={e => updField("billing_period", e.target.value)}
                  >
                    <option value="weekly"    style={{ background: "#0A0A0A", color: "#F5F2EB" }}>Weekly</option>
                    <option value="biweekly"  style={{ background: "#0A0A0A", color: "#F5F2EB" }}>Biweekly (every 2 weeks)</option>
                    <option value="monthly"   style={{ background: "#0A0A0A", color: "#F5F2EB" }}>Monthly</option>
                    <option value="quarterly" style={{ background: "#0A0A0A", color: "#F5F2EB" }}>Quarterly (every 3 months)</option>
                  </select>
                </Field>
                <div className="col-span-2">
                  <Field label="Billing address">
                    <input data-testid="edit-billing-address" className="field-input" value={edit.billing_address} onChange={e => updField("billing_address", e.target.value)}/>
                  </Field>
                </div>
                <div className="col-span-2 flex justify-end">
                  <button
                    data-testid="save-details"
                    onClick={saveDetails}
                    disabled={savingEdit}
                    className="btn-gold !py-2 !px-4 text-[11px]"
                  >{savingEdit ? "Saving…" : "Save changes"}</button>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="overline mb-3">Employees ({employees.length})</div>
            <div className="space-y-2 mb-3">
              {employees.map(e => (
                <div key={e} className="flex justify-between items-center text-sm py-2 border-b border-white/5">
                  <span>{e}</span>
                  {canManage && <button data-testid={`remove-emp-${e}`} onClick={() => removeEmp(e)} className="text-[#71717A] hover:text-red-400"><Trash2 size={12}/></button>}
                </div>
              ))}
              {employees.length === 0 && <div className="text-xs text-[#71717A]">No employees yet.</div>}
            </div>
            {canManage && (
              <div className="flex gap-2">
                <input data-testid="add-emp-email" type="email" placeholder="employee@company.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} className="field-input flex-1"/>
                <button data-testid="add-emp-btn" onClick={addEmp} className="btn-gold !py-2 !px-3 text-[11px] shrink-0"><UserPlus size={12}/></button>
              </div>
            )}
          </div>

          <CorpPaymentMethods account={account} canManage={canManage}/>

          <CorpInvoices account={account} refreshSignal={invoicesRefresh}/>

          <div>
            <div className="overline mb-3">Consolidated invoice</div>

            {/* Period selector — order: Weekly · Monthly (default, centered) · Custom.
                Active tab gets a bright solid-gold pill with glow for fast scanning. */}
            <div className="flex justify-center gap-1 mb-3">
              {[
                { v: "week",  l: "Weekly"  },
                { v: "month", l: "Monthly" },
                { v: "range", l: "Custom"  },
              ].map(opt => (
                <button
                  key={opt.v}
                  data-testid={`period-${opt.v}`}
                  onClick={() => { setInvPeriodType(opt.v); setInvoice(null); }}
                  className={`flex-1 max-w-[140px] py-2 text-[10px] uppercase tracking-[0.18em] border transition-colors ${invPeriodType === opt.v ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-bold shadow-[0_0_18px_rgba(212,175,55,0.6)]" : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"}`}
                >{opt.l}</button>
              ))}
            </div>

            <div className="flex gap-2 mb-4">
              {invPeriodType === "month" && (
                <input data-testid="invoice-month" type="month" value={invMonth} onChange={e => setInvMonth(e.target.value)} className="field-input flex-1"/>
              )}
              {invPeriodType === "week" && (
                <div className="flex-1">
                  <label className="block text-[9px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Week starting</label>
                  <input data-testid="invoice-week" type="date" value={invWeekStart} onChange={e => setInvWeekStart(e.target.value)} className="field-input w-full"/>
                </div>
              )}
              {invPeriodType === "range" && (
                <div className="flex gap-2 flex-1">
                  <div className="flex-1">
                    <label className="block text-[9px] uppercase tracking-[0.2em] text-[#71717A] mb-1">From</label>
                    <input data-testid="invoice-range-start" type="date" value={invRangeStart} onChange={e => setInvRangeStart(e.target.value)} className="field-input w-full"/>
                  </div>
                  <div className="flex-1">
                    <label className="block text-[9px] uppercase tracking-[0.2em] text-[#71717A] mb-1">To</label>
                    <input data-testid="invoice-range-end" type="date" value={invRangeEnd} onChange={e => setInvRangeEnd(e.target.value)} className="field-input w-full"/>
                  </div>
                </div>
              )}
              <button data-testid="load-invoice-btn" onClick={loadInvoice} disabled={busy} className="btn-gold !py-2 !px-4 text-[11px] shrink-0 self-end"><FileText size={12}/>{busy ? "…" : "View"}</button>
            </div>

            {invoice && (
              <div data-testid="invoice-result" className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-[#A1A1AA] text-xs uppercase tracking-[0.18em]">Period</span><span>{invoice.period_label || invoice.period}</span></div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="border border-white/5 bg-black/30 p-2"><div className="text-[10px] text-[#71717A] uppercase tracking-[0.18em]">Rides</div><div className="font-serif text-xl text-[#F5F2EB]">{invoice.ride_count}</div></div>
                  <div className="border border-white/5 bg-black/30 p-2"><div className="text-[10px] text-emerald-300/70 uppercase tracking-[0.18em]">Paid</div><div className="font-serif text-xl text-emerald-300">{invoice.paid_count}</div><div className="text-[10px] text-emerald-300/60">${(invoice.paid_subtotal || 0).toFixed(2)}</div></div>
                  <div className="border border-white/5 bg-black/30 p-2"><div className="text-[10px] text-amber-300/70 uppercase tracking-[0.18em]">Unpaid</div><div className="font-serif text-xl text-amber-300">{invoice.unpaid_count}</div><div className="text-[10px] text-amber-300/60">${(invoice.unpaid_subtotal || 0).toFixed(2)}</div></div>
                </div>
                <div className="flex justify-between pt-3 border-t border-white/10"><span className="text-[#A1A1AA] text-xs uppercase tracking-[0.18em]">Subtotal</span><span>${invoice.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA] text-xs uppercase tracking-[0.18em]">{invoice.discount_percent}% discount</span><span>−${invoice.discount_amount.toFixed(2)}</span></div>
                <div className="flex justify-between">
                  <span className="overline">Total due</span>
                  <span className="font-serif text-2xl text-[#D4AF37]">${invoice.total_due.toFixed(2)}</span>
                </div>

                {/* Per-trip table with checkboxes */}
                {invoice.rides.length > 0 && (
                  <div className="border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="overline">Trips in this period</div>
                      <div className="flex gap-2 text-[10px] uppercase tracking-[0.18em]">
                        <button data-testid="trip-select-unpaid" onClick={selectAllUnpaid} className="text-[#D4AF37] hover:underline">Select all unpaid</button>
                        {selectedTripIds.size > 0 && <button onClick={clearSelection} className="text-[#71717A] hover:underline">Clear ({selectedTripIds.size})</button>}
                      </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto border border-white/5">
                      <table className="w-full text-[11px]">
                        <thead className="bg-black/40 text-[9px] uppercase tracking-[0.18em] text-[#71717A]">
                          <tr>
                            <th className="text-left py-1.5 px-2 w-6"></th>
                            <th className="text-left px-2">Date · Code</th>
                            <th className="text-left px-2">Passenger</th>
                            <th className="text-right px-2">Fare</th>
                            <th className="text-center px-2">Status</th>
                            <th className="text-left px-2">Method</th>
                            <th className="text-left px-2">Transaction ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.rides.map(t => {
                            const paid = t.payment_status === "paid";
                            const txId = t.payment_id || "";
                            return (
                              <tr key={t.booking_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                                <td className="px-2 py-1.5">
                                  <input
                                    type="checkbox"
                                    data-testid={`trip-cb-${t.booking_id}`}
                                    checked={selectedTripIds.has(t.booking_id)}
                                    onChange={() => toggleTrip(t.booking_id)}
                                    className="accent-[#D4AF37]"
                                  />
                                </td>
                                <td className="px-2 text-[#F5F2EB]">
                                  <div>{new Date(t.pickup_datetime).toLocaleDateString()} · <span className="font-mono text-[#D4AF37]">{t.confirmation_code}</span></div>
                                </td>
                                <td className="px-2 text-[#A1A1AA] truncate max-w-[140px]">{t.customer_name}</td>
                                <td className="px-2 text-right font-mono">${Number(t.fare || 0).toFixed(2)}</td>
                                <td className="px-2 text-center">
                                  {paid ? (
                                    <span className="text-[9px] px-1.5 py-0.5 uppercase tracking-[0.15em] border text-emerald-300 border-emerald-500/40">
                                      Paid
                                    </span>
                                  ) : (
                                    <button
                                      data-testid={`trip-pay-${t.booking_id}`}
                                      onClick={() => setPayTrip(t)}
                                      title="Add card or take a payment for this trip"
                                      className="text-[9px] px-1.5 py-0.5 uppercase tracking-[0.15em] border text-amber-300 border-amber-500/40 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 transition-colors"
                                    >
                                      {(t.payment_status || "pay later").replace(/_/g, " ")}
                                    </button>
                                  )}
                                </td>
                                <td className="px-2 text-[#A1A1AA] uppercase tracking-[0.1em] text-[10px]">
                                  {paid ? (t.payment_method || "—").replace(/_/g, " ") : <span className="text-[#71717A]">—</span>}
                                </td>
                                <td className="px-2 font-mono text-[10px]">
                                  {paid && txId ? (
                                    <button
                                      data-testid={`trip-tx-${t.booking_id}`}
                                      onClick={() => { navigator.clipboard.writeText(txId); toast.success("Transaction ID copied"); }}
                                      title={`Copy ${txId}`}
                                      className="text-[#D4AF37] hover:underline"
                                    >{txId.slice(0, 12)}…</button>
                                  ) : <span className="text-[#71717A]">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        data-testid="create-custom-invoice"
                        onClick={createCustomInvoice}
                        disabled={selectedTripIds.size === 0 || billing}
                        className="btn-gold !py-2 !px-4 text-[11px] flex-1 justify-center"
                      >
                        <FileText size={12}/> {billing ? "Creating…" : `Create Invoice for ${selectedTripIds.size || "selected"} Trip${selectedTripIds.size === 1 ? "" : "s"}`}
                      </button>
                    </div>

                    {/* Just-created invoice — show pay link + Pay / Copy / Email / SMS buttons */}
                    {createdInvoice && (
                      <div data-testid="created-invoice-panel" className="mt-3 border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">Invoice created</div>
                            <div className="font-mono text-[#F5F2EB]">{createdInvoice.invoice_number} · ${Number(createdInvoice.total || createdInvoice.subtotal || 0).toFixed(2)}</div>
                          </div>
                          <button
                            onClick={() => setCreatedInvoice(null)}
                            className="text-[#71717A] hover:text-white text-base px-1"
                          >×</button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <a
                            data-testid="ci-pay-now"
                            href={createdInvoice.payment_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-gold !py-1.5 !px-3 text-[10px]"
                          ><ExternalLink size={11}/> Pay Now</a>
                          <button
                            data-testid="ci-copy-link"
                            onClick={() => { navigator.clipboard.writeText(createdInvoice.payment_url); toast.success("Pay link copied"); }}
                            className="btn-ghost !py-1.5 !px-3 text-[10px]"
                          ><Copy size={11}/> Copy pay link</button>
                          <button
                            data-testid="ci-resend-email"
                            onClick={async () => {
                              try { await api.post(`/invoices/consolidated/${createdInvoice.invoice_id}/email`); toast.success(`Re-emailed to ${account.billing_email}`); }
                              catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
                            }}
                            className="btn-ghost !py-1.5 !px-3 text-[10px]"
                          ><Mail size={11}/> Resend email</button>
                          {account.billing_phone && (
                            <button
                              data-testid="ci-text-sms"
                              onClick={async () => {
                                try { await api.post(`/invoices/consolidated/${createdInvoice.invoice_id}/sms`); toast.success(`Texted to ${account.billing_phone}`); }
                                catch (e) { toast.error(e?.response?.data?.detail || "SMS failed"); }
                              }}
                              className="btn-ghost !py-1.5 !px-3 text-[10px]"
                            ><MessageSquare size={11}/> Text SMS</button>
                          )}
                        </div>
                        <div className="text-[10px] text-[#A1A1AA] font-mono break-all bg-black/40 p-2 border border-white/5">
                          {createdInvoice.payment_url}
                        </div>
                        <div className="text-[10px] text-[#A1A1AA]">
                          Customer can pay via <span className="text-[#D4AF37]">Card · Apple Pay · Google Pay · Zelle · Venmo · Cash</span> on this link.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button data-testid="download-invoice-csv" onClick={downloadCsv} className="btn-ghost w-full mt-2 justify-center text-[11px]"><Download size={12}/>Download CSV</button>
              </div>
            )}
          </div>

          {isDispatcher && (
            <button data-testid="delete-corp-btn" onClick={deleteAcc} className="btn-ghost !text-red-400 !border-red-400/30 w-full justify-center"><Trash2 size={12}/>Delete account</button>
          )}
        </div>
      </div>

      {/* Pay-now modal — opens when a dispatcher/corp admin clicks an unpaid pill
          in the trips table. Embeds the full PaymentSelector so a card can be
          added on the spot (or Zelle/Venmo/Cash logged). */}
      {payTrip && (
        <div
          data-testid="corp-pay-modal"
          className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPayTrip(null)}
        >
          <div
            className="bg-[#050505] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="corp-pay-modal-close"
              onClick={() => setPayTrip(null)}
              className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white"
              aria-label="Close"
            ><X size={18}/></button>
            <div className="overline mb-1">Take payment</div>
            <div className="font-serif text-2xl text-[#D4AF37] mb-1">{payTrip.confirmation_code}</div>
            <div className="text-xs text-[#A1A1AA] mb-4">{payTrip.customer_name} · ${Number(payTrip.fare || 0).toFixed(2)}</div>
            <PaymentSelector
              booking={payTrip}
              dispatcherMode={isDispatcher}
              onPaid={() => { setPayTrip(null); loadInvoice(); toast.success("Payment recorded"); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><div className="field-label">{label}</div>{children}</div>;
}
